import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayoutNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, MapPin, Home, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: '',
    starting_price: '',
    map_image: '',
    thumbnail: '',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, { headers: getAuthHeaders() });
      setProjects(response.data.filter(p => p.id === 'bakrajo-hills'));
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleOpenDialog = (project = null) => {
    if (project) {
      setSelectedProject(project);
      setFormData({
        name: project.name,
        location: project.location,
        description: project.description,
        starting_price: project.starting_price,
        map_image: project.map_image || '',
        thumbnail: project.thumbnail || '',
        status: project.status
      });
    } else {
      setSelectedProject(null);
      setFormData({
        name: '',
        location: '',
        description: '',
        starting_price: '',
        map_image: '',
        thumbnail: '',
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.location || !formData.starting_price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (selectedProject) {
        await axios.put(
          `${API_URL}/api/projects/${selectedProject.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Project updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/projects`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Project created successfully');
      }
      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProject) return;

    try {
      await axios.delete(
        `${API_URL}/api/projects/${selectedProject.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Project deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-10" data-testid="admin-projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 font-light">Management</p>
            <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">Projects</h1>
          </div>
          <button
            onClick={() => handleOpenDialog()}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="add-project-btn"
          >
            <Plus className="w-4 h-4" />
            Add Project
          </button>
        </div>

        {/* Projects List */}
        <div className="border border-white/10">
          {loading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : projects.length > 0 ? (
            <div className="divide-y divide-white/10">
              {projects.map((project) => (
                <div key={project.id} className="flex items-center gap-4 p-4 lg:p-6 hover:bg-white/5 transition-colors" data-testid={`project-row-${project.id}`}>
                  <div className="w-16 h-16 bg-white/5 overflow-hidden flex-shrink-0">
                    <img
                      src={project.thumbnail || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=100&q=80'}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate">{project.name}</h4>
                    <div className="flex items-center gap-2 text-white/40 text-xs font-light mt-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{project.location}</span>
                    </div>
                  </div>
                  <div className="hidden md:block text-center px-6">
                    <p className="text-white font-light">{project.starting_price}</p>
                    <p className="text-white/40 text-[10px] uppercase tracking-[0.1em] font-light">Starting</p>
                  </div>
                  <div className="hidden md:flex items-center gap-2 px-6">
                    <Home className="w-4 h-4 text-white/40" />
                    <span className="text-white font-light">{project.total_units || 0}</span>
                  </div>
                  <div className="hidden md:block px-6">
                    <span className={`px-3 py-1 text-[10px] uppercase tracking-widest font-light ${
                      project.status === 'completed' 
                        ? 'bg-white text-black' 
                        : 'border border-white/30 text-white/70'
                    }`}>
                      {project.status === 'active' ? 'Ongoing' : project.status === 'completed' ? 'Completed' : 'Upcoming'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/map-editor/${project.id}`}
                      className="p-2 text-white/40 hover:text-green-500 transition-colors"
                      title="Edit Map"
                    >
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={() => handleOpenDialog(project)}
                      className="p-2 text-white/40 hover:text-white transition-colors"
                      data-testid={`edit-project-${project.id}`}
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedProject(project);
                        setDeleteDialogOpen(true);
                      }}
                      className="p-2 text-white/40 hover:text-red-500 transition-colors"
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-16 text-center">
              <Home className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 font-light mb-6">No projects yet</p>
              <button onClick={() => handleOpenDialog()} className="btn-primary">
                Create Your First Project
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl rounded-none" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white tracking-tight">
              {selectedProject ? 'Edit Project' : 'Add New Project'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Project Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Dli Shar"
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Starting Price *</Label>
                <Input
                  value={formData.starting_price}
                  onChange={(e) => setFormData({ ...formData, starting_price: e.target.value })}
                  placeholder="e.g., $120,000"
                  className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                  data-testid="project-price-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Location *</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Sulaymaniyah - Salim Street"
                className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                data-testid="project-location-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Project description..."
                className="bg-transparent border-white/20 text-white rounded-none min-h-[100px] font-light"
                data-testid="project-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none h-12" data-testid="project-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/20 rounded-none">
                  <SelectItem value="active">Ongoing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Thumbnail Image URL</Label>
              <Input
                value={formData.thumbnail}
                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                data-testid="project-thumbnail-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-light">Map Render Image URL</Label>
              <Input
                value={formData.map_image}
                onChange={(e) => setFormData({ ...formData, map_image: e.target.value })}
                placeholder="https://example.com/map.jpg"
                className="bg-transparent border-white/20 text-white rounded-none h-12 font-light"
                data-testid="project-map-input"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="btn-outline px-6 py-3"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary px-6 py-3"
              data-testid="save-project-btn"
            >
              {saving ? 'Saving...' : selectedProject ? 'Update Project' : 'Create Project'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 rounded-none" data-testid="delete-project-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Project</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 font-light">
            Are you sure you want to delete <span className="text-white">{selectedProject?.name}</span>?
            This will also delete all units associated with this project.
          </p>
          <DialogFooter>
            <button
              onClick={() => setDeleteDialogOpen(false)}
              className="btn-outline px-6 py-3"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="bg-red-600 text-white px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete Project
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
